"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMexicoDay = getMexicoDay;
exports.getShiftDay = getShiftDay;
exports.addDaysToDay = addDaysToDay;
exports.getFortnight = getFortnight;
exports.evaluateAttendanceCorrection = evaluateAttendanceCorrection;
const attendance_correction_enums_1 = require("../constants/attendance-correction.enums");
const MEXICO_TZ = 'America/Mexico_City';
let mexicoFormatter;
function getMexicoParts(value) {
    mexicoFormatter ?? (mexicoFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: MEXICO_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }));
    const parts = mexicoFormatter.formatToParts(new Date(value));
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return {
        day: `${get('year')}-${get('month')}-${get('day')}`,
        minutes: Number(get('hour')) * 60 + Number(get('minute')),
    };
}
/** Calendar day (`YYYY-MM-DD`) of an instant, in Mexico time */
function getMexicoDay(value) {
    return getMexicoParts(value).day;
}
/** Calendar day (`YYYY-MM-DD`) of a stored shift date — see `AttendanceCorrectionInput.shiftDate` */
function getShiftDay(shiftDate) {
    if (typeof shiftDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(shiftDate))
        return shiftDate;
    return new Date(shiftDate).toISOString().slice(0, 10);
}
/** Adds (or subtracts) calendar days to a `YYYY-MM-DD` day */
function addDaysToDay(day, days) {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
/** Payroll fortnight (1–15 or 16–last day) that contains a `YYYY-MM-DD` day */
function getFortnight(day) {
    const [y, m, d] = day.split('-').map(Number);
    const month = day.slice(0, 7);
    if (d <= 15)
        return { start: `${month}-01`, end: `${month}-15` };
    const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return { start: `${month}-16`, end: `${month}-${String(lastDay).padStart(2, '0')}` };
}
function toMinutes(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
}
function formatMinutes(minutes) {
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}
/** `2026-09-25` → `25/09/2026` */
function formatDay(day) {
    const [y, m, d] = day.split('-');
    return `${d}/${m}/${y}`;
}
/**
 * Evaluates the effective times of an attendance correction.
 *
 * Pure: the caller resolves what needs the database (the employment start, whether the
 * payroll is paid) and merges the request with the stored record before calling.
 */
function evaluateAttendanceCorrection(input) {
    const errors = [];
    const warnings = [];
    const shiftDay = getShiftDay(input.shiftDate);
    const referenceDay = getMexicoDay(input.referenceDate);
    const start = input.startTime ? getMexicoParts(input.startTime) : null;
    const end = input.endTime ? getMexicoParts(input.endTime) : null;
    // --- Date ---
    const isFuture = shiftDay > referenceDay;
    if (isFuture) {
        errors.push({
            code: attendance_correction_enums_1.AttendanceCorrectionIssueCode.FutureDate,
            message: `El ${formatDay(shiftDay)} todavía no ha llegado.`,
        });
    }
    if (input.employmentStartDate) {
        const employmentDay = getMexicoDay(input.employmentStartDate);
        if (shiftDay < employmentDay) {
            errors.push({
                code: attendance_correction_enums_1.AttendanceCorrectionIssueCode.BeforeEmploymentStart,
                message: `El ${formatDay(shiftDay)} es anterior al alta (${formatDay(employmentDay)}).`,
            });
        }
    }
    if (!isFuture) {
        const fortnight = getFortnight(shiftDay);
        const lastOnTimeDay = addDaysToDay(fortnight.end, attendance_correction_enums_1.ATTENDANCE_CORRECTION_GRACE_DAYS);
        if (referenceDay > lastOnTimeDay) {
            warnings.push({
                code: attendance_correction_enums_1.AttendanceCorrectionIssueCode.OutOfPeriod,
                message: `El ${formatDay(shiftDay)} es de la quincena del ${formatDay(fortnight.start)} al ` +
                    `${formatDay(fortnight.end)}; lo normal es corregirlo dentro de esa quincena o en los ` +
                    `${attendance_correction_enums_1.ATTENDANCE_CORRECTION_GRACE_DAYS} días siguientes.`,
            });
        }
    }
    // --- Times ---
    if (input.requireStartTime && !start) {
        errors.push({
            code: attendance_correction_enums_1.AttendanceCorrectionIssueCode.MissingStartTime,
            message: 'Falta la hora de entrada.',
        });
    }
    const outsideDay = [
        { label: 'entrada', parts: start },
        { label: 'salida', parts: end },
    ].filter((t) => t.parts && t.parts.day !== shiftDay);
    for (const t of outsideDay) {
        errors.push({
            code: attendance_correction_enums_1.AttendanceCorrectionIssueCode.TimeOutsideShiftDay,
            message: `La hora de ${t.label} cae el ${formatDay(t.parts.day)}, no el ${formatDay(shiftDay)}.`,
        });
    }
    let workedHours = null;
    if (input.startTime && input.endTime && start && end) {
        const diffMs = new Date(input.endTime).getTime() - new Date(input.startTime).getTime();
        workedHours = Math.round((diffMs / 3600000) * 100) / 100;
        if (diffMs <= 0) {
            errors.push({
                code: attendance_correction_enums_1.AttendanceCorrectionIssueCode.EndNotAfterStart,
                message: `La salida (${formatMinutes(end.minutes)}) no es posterior a la entrada ` +
                    `(${formatMinutes(start.minutes)}). Las horas van en formato de 24 h: las 3 de la tarde son las 15:00.`,
            });
        }
    }
    if (start && start.minutes < toMinutes(attendance_correction_enums_1.ATTENDANCE_UNUSUAL_START_BEFORE)) {
        warnings.push({
            code: attendance_correction_enums_1.AttendanceCorrectionIssueCode.UnusualStart,
            message: `Entrada a las ${formatMinutes(start.minutes)}: es antes de las ${attendance_correction_enums_1.ATTENDANCE_UNUSUAL_START_BEFORE}.`,
        });
    }
    if (end && end.minutes > toMinutes(attendance_correction_enums_1.ATTENDANCE_UNUSUAL_END_AFTER)) {
        warnings.push({
            code: attendance_correction_enums_1.AttendanceCorrectionIssueCode.UnusualEnd,
            message: `Salida a las ${formatMinutes(end.minutes)}: es después de las ${attendance_correction_enums_1.ATTENDANCE_UNUSUAL_END_AFTER}.`,
        });
    }
    return { errors, warnings, workedHours };
}
